import React, { useCallback, useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { track } from '../analytics.js';
import './Stripe.css';
import './PhoneHandoff.css';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const money = (cents) => new Intl.NumberFormat('en-US', { currency: 'USD', style: 'currency' }).format(cents / 100);

async function post(path, body) {
  const res = await fetch(BACKEND + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return res.json();
}

// The phone half of "Pay on your phone" (/?pay=<token>): the order was filled
// out on another screen and saved server-side; this page only takes payment
// for that same PaymentIntent and then asks the backend to ship the saved
// order. The first screen learns about it from /order-status.
function PayForm({ token, nonce, order, onDone }) {
  const stripe = useStripe();
  const elements = useElements();
  const [email, setEmail] = useState(order.email || '');
  const [name, setName] = useState('');
  const [amount, setAmount] = useState(order.amount);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || busy) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Please enter your email address for the receipt.');
      return;
    }
    setBusy(true);
    setError('');

    // The first screen may have repriced since this page loaded (promo code,
    // destination country). Stripe charges the PaymentIntent's current amount
    // either way; refresh so the button never shows a stale price.
    try {
      const fresh = await post('/handoff/get', { nonce, token });
      if (fresh.completed || fresh.status === 'succeeded') { onDone(email.trim()); return; }
      if (fresh.amount && fresh.amount !== amount) {
        setAmount(fresh.amount);
        await elements.fetchUpdates();
        setBusy(false);
        setError('The price was updated to ' + money(fresh.amount) + '. Tap pay again to confirm.');
        return;
      }
    } catch (err) { /* fall through: confirmPayment is the authority */ }

    // Remembered across a redirect-based payment method's round trip.
    try { sessionStorage.setItem('ollie_pay_email', email.trim()); } catch (err) { /* ignore */ }

    const { error: payError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
      confirmParams: {
        return_url: window.location.href,
        payment_method_data: { billing_details: { name: name || undefined, email: email.trim() } },
      },
    });

    if (payError) {
      setBusy(false);
      setError(payError.message || 'Payment failed. Please try again.');
      return;
    }
    if (paymentIntent && paymentIntent.status === 'succeeded') {
      onDone(email.trim());
      return;
    }
    setBusy(false);
    setError('Payment is still processing. Please wait a moment and try again.');
  };

  return (
    <form className="stripeform" onSubmit={handleSubmit}>
      <div className="form-row">
        <label htmlFor="payEmail">Email<small>(for receipt)</small></label>
        <input type="email" id="payEmail" autoComplete="email" value={email}
          onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email address" />
      </div>
      <div className="form-row">
        <label htmlFor="payName">Your name<small>(for payment)</small></label>
        <input type="text" id="payName" autoComplete="name" value={name}
          onChange={(e) => setName(e.target.value)} placeholder="Enter your full name" />
      </div>
      <div className="payment-element-container">
        <PaymentElement options={{ layout: 'tabs', fields: { billingDetails: { name: 'never', email: 'never' } } }} />
      </div>
      <button type="submit" className={busy ? 'loading' : ''} disabled={busy || !stripe || !elements}>
        {busy ? 'Processing…' : `Pay ${money(amount)}`}
      </button>
      {error && <div id="payment-message" className="error">{error}</div>}
    </form>
  );
}

export default function PhonePay({ token }) {
  const [nonce, setNonce] = useState('');
  const [order, setOrder] = useState(null);
  // loading | pay | shipping | done | error
  const [phase, setPhase] = useState('loading');
  const [message, setMessage] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');

  const ship = useCallback(async (n, email) => {
    setPhase('shipping');
    try {
      const result = await post('/handoff/complete', { nonce: n, token, email });
      if (result && result.id) {
        setPreviewUrl(`${window.location.origin}/?pdf=${encodeURIComponent(result.id)}`);
        setPhase('done');
        track('purchase_completed', { via: 'pay_on_phone', postcard_id: result.id, cost: result.cost });
        return;
      }
    } catch (e) { /* handled below */ }
    setMessage("Your payment went through but we hit a problem submitting your postcard. Please contact support@olliemail.net and we'll sort it out.");
    setPhase('error');
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const n = (await post('/nonce', {})).nonce;
        const data = await post('/handoff/get', { nonce: n, token });
        if (cancelled) return;
        setNonce(n);
        if (data.result !== 'success') {
          setMessage('This payment link has expired. Please start again from the screen you scanned.');
          setPhase('error');
          return;
        }
        setOrder(data);
        if (data.completed) { setPhase('done'); return; }
        // Paid but not shipped: back from a redirect-based payment method, or
        // a retry after a dropped connection. Finish the job.
        if (data.status === 'succeeded') {
          let saved = '';
          try { saved = sessionStorage.getItem('ollie_pay_email') || ''; } catch (e) { /* ignore */ }
          ship(n, saved || data.email);
          return;
        }
        setPhase('pay');
      } catch (e) {
        if (!cancelled) { setMessage('Could not load this order. Check your connection and scan the code again.'); setPhase('error'); }
      }
    })();
    return () => { cancelled = true; };
  }, [token, ship]);

  return (
    <div className="App phone-pay">
      <div className="head">
        <h1>Pay for your postcard</h1>
      </div>
      {order && order.imgUrl && (
        <div className="phone-pay-art"><img alt="Your postcard artwork" src={order.imgUrl} /></div>
      )}
      <div className="phone-pay-body stripecontainer">
        {phase === 'loading' && <p>Loading your order…</p>}
        {phase === 'shipping' && <p>Payment received &mdash; sending your postcard…</p>}
        {phase === 'error' && <div id="payment-message" className="error">{message}</div>}
        {phase === 'done' && (
          <div id="payment-message" className="success">
            Payment successful! You&rsquo;ll receive an email receipt shortly.
            {previewUrl && <> <a href={previewUrl} target="_blank" rel="noopener noreferrer">Preview your postcard here</a>.</>}
            {' '}Please allow 10-14 business days for delivery. You can close this page &mdash; the other screen has been updated.
          </div>
        )}
        {phase === 'pay' && order && (
          <>
            <p className="phone-pay-summary">
              {order.size.replace('x', ' × ')} in postcard{order.recipientName ? <> to <strong>{order.recipientName}</strong></> : null}. The address was entered on the other screen; this page only takes payment.
            </p>
            <Elements stripe={stripePromise} options={{ clientSecret: order.client_secret }}>
              <PayForm token={token} nonce={nonce} order={order} onDone={(email) => ship(nonce, email)} />
            </Elements>
          </>
        )}
      </div>
    </div>
  );
}
